import type { FontLoadStrategy, Overwrite, Style } from '@wix/thunderbolt-becky-types'
import type { FontValue } from '@wix/css-property-parser'
import { Font } from '@wix/css-property-parser'
import { fixFontWithSpaceOrNotLatinChars } from './fontFixer'
import { isWeightBold } from './isWeightBold'
// TODO: uncomment when FontObject type includes sizeUnit
// import type { FontObject } from '@wix/thunderbolt-components-native'

export interface FontObject {
	style: string
	variant: string
	weight: string
	size: number
	sizeUnit?: string
	lineHeight: string
	family: Array<string>
	bold: boolean
	italic: boolean
}

const woff2Regex = new RegExp('(\\/\\/[^"]*.\\.woff2)')
const woff1Regex = new RegExp('(\\/\\/[^("]+]*.\\.woff)')

export const patchFontDisplay = (
	fontDisplay: FontLoadStrategy,
	fontFamilyToFontFaces: Record<string, Array<string>>
): Record<string, Array<string>> => {
	const patchedFonts = { ...fontFamilyToFontFaces }
	Object.keys(patchedFonts).forEach((font) => {
		patchedFonts[font] = patchedFonts[font].map((fontString) =>
			fontString.replace(/[ \t]*font-display:[^;]*;\n?/, '').replace('}', `font-display: ${fontDisplay};\n}`)
		)
	})
	return patchedFonts
}

const invalidFonObj: FontObject = {
	style: '',
	variant: '',
	weight: '',
	size: -1,
	lineHeight: '',
	family: [],
	bold: false,
	italic: false,
}

export const extractFromQuotes = (str: string): string => {
	const match = str.match(/'(?<strInQuotes>.*?)'|"(?<strInDoubleQuotes>.*?)"/)

	if (match) {
		return match.groups!.strInQuotes || match.groups!.strInDoubleQuotes
	}

	return str
}

const fontRegEx = /(?<style>.*?)\s(?<variant>.*?)\s(?<weight>.*?)\s(?<size>.*?)\s(?<fullFontFamily>.*)/

const isValidFont = (font: string): boolean => fontRegEx.test(font)

export const parseFontStr = (font: string): FontObject => {
	const match = font.match(fontRegEx)
	if (!match) {
		return invalidFonObj
	}

	const { style, variant, weight, size, fullFontFamily } = match.groups!
	const sizeSplit = size ? size.split('/') : []
	const fullFontFamilyArr = fullFontFamily.split(',').map((currFamily) => extractFromQuotes(currFamily))
	const sizeValue = sizeSplit[0].replace(/[a-z|%]+/, '')
	const sizeUnit = sizeSplit[0].replace(sizeValue, '')
	return {
		style,
		variant,
		weight,
		size: parseInt(sizeValue, 10),
		sizeUnit,
		lineHeight: sizeSplit[1],
		family: fullFontFamilyArr,
		bold: isWeightBold(weight),
		italic: style === 'italic',
	}
}

export const parseFontWithCalc = (font: string): Overwrite<FontObject, { size: string | number }> => {
	const [_, calcExpression] = /(calc\(.*\))/.exec(font) ?? []
	if (calcExpression) {
		const zeroPixelFont = font.replace(calcExpression, '0px')
		const parsedFont = parseFontStr(zeroPixelFont)
		return {
			...parsedFont,
			size: calcExpression,
			sizeUnit: '',
		}
	}
	return parseFontStr(font)
}

export function fontObjectToString(
	{
		style,
		variant,
		weight,
		size,
		sizeUnit = 'px',
		lineHeight,
		family,
	}: Omit<FontObject, 'size'> & { size: string | number },
	respectSizeUnit?: boolean // TODO: remove when SPX is fully merged
): string {
	const fullFontFamily = fixFontWithSpaceOrNotLatinChars(family.join(','))
	const sizeUnitToUse = respectSizeUnit ? sizeUnit : 'px'
	return `${style} ${variant} ${weight} ${size}${sizeUnitToUse}/${lineHeight} ${fullFontFamily}`
}

const colorRegEx = /(({(?<themeColor>.*)})|(?<customColor>#[A-Za-z0-9]+)|(?<themeColorWithoutCurlyBrackets>color_[0-9]+))$/
export const getFontWithoutColor = (str: string) => str && str.replace(colorRegEx, '').trim()

export const getFontColor = (str: string = '') => {
	const match = str.match(colorRegEx)
	if (!match) {
		return undefined
	}

	const { themeColor, customColor, themeColorWithoutCurlyBrackets } = match.groups!
	return themeColor || customColor || themeColorWithoutCurlyBrackets
}

const resolvedSpxRegex = new RegExp(/calc\(.*\)|clamp\(.*\)|max\(.*\)|min\(.*\)/, 'g')

export const getFontFamilies = (font: string): Array<string> => {
	const fixedFont = font.replace(resolvedSpxRegex, '0px')
	return isValidFont(fixedFont) ? parseFontStr(fixedFont).family : []
}

export const extractFontFamiliesFromCssProperties = (cssProperties: Style['cssProperties']) => {
	const fontFamilies: Array<string> = []

	try {
		if (cssProperties?.font) {
			const parsedFont: FontValue | null = cssProperties.font ? Font.parse(cssProperties.font as string) : null

			if (parsedFont && 'fontFamily' in parsedFont && parsedFont.fontFamily) {
				if (parsedFont.fontFamily.type === 'font-list') {
					// push all the font families
					parsedFont.fontFamily?.families?.forEach((family: string) => {
						fontFamilies.push(extractFromQuotes(family.trim()))
					})
				}
			}
		}

		if (cssProperties?.fontFamily) {
			cssProperties.fontFamily.split(',').forEach((family: string) => {
				fontFamilies.push(extractFromQuotes(family.trim()))
			})
		}
	} catch (error) {
		// ignore error
		console.error('Error extracting font families from css properties', error)
	}

	return fontFamilies
}

function urlExtractor(font: string, urlExtractorRgx: RegExp) {
	const fontUrlMatch = urlExtractorRgx.exec(font)

	return fontUrlMatch ? fontUrlMatch[0] : ''
}

export const getWoff2FontUrl = (font: string): string => urlExtractor(font, woff2Regex)

export const getWoff1FontUrl = (font: string): string => urlExtractor(font, woff1Regex)
